import { ref } from "vue";

let ToneModule: typeof import("tone") | null = null;
async function getTone() {
    if (!ToneModule) {
        ToneModule = await import("tone");
    }
    return ToneModule;
}

const isMusicPlaying = ref(false);

const BPM = 54;
const FADE_IN = 2.5;
const FADE_OUT = 1.5;

const SCALES: number[][] = [
    [0, 2, 4, 7, 9],
    [0, 3, 5, 7, 10],
    [0, 2, 3, 7, 8],
    [0, 2, 3, 7, 9],
];

const ROOT_POOL = [45, 47, 48, 50, 52, 53, 55];

const KALIMBA_RANGE: [number, number] = [55, 79];
const BELLS_RANGE: [number, number] = [72, 93];

const rand = (min: number, max: number) => min + Math.random() * (max - min);
const randInt = (min: number, max: number) => Math.floor(rand(min, max + 1));
const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];
const chance = (p: number) => Math.random() < p;

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const midiToNote = (midi: number) => `${NOTE_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;

function reflect(value: number, length: number): number {
    const max = length - 1;
    if (max <= 0) return 0;
    let v = value;
    while (v < 0 || v > max) {
        if (v < 0) v = -v;
        if (v > max) v = 2 * max - v;
    }
    return v;
}

function buildScaleNotes(root: number, scale: number[], [low, high]: [number, number]): number[] {
    const notes: number[] = [];
    for (let octave = -3; octave <= 6; octave++) {
        for (const step of scale) {
            const note = root + step + octave * 12;
            if (note >= low && note <= high) {
                notes.push(note);
            }
        }
    }
    return notes.sort((a, b) => a - b);
}

interface Scene {
    root: number;
    scale: number[];
}

function nextScene(prev: Scene): Scene {
    if (chance(0.45)) {
        return { root: prev.root, scale: pick(SCALES.filter((s) => s !== prev.scale)) };
    }
    const shift = pick([-5, -3, -2, 2, 3, 5]);
    return { root: Math.min(57, Math.max(45, prev.root + shift)), scale: pick(SCALES) };
}

interface ZenEngine {
    fadeIn(): void;
    fadeOut(): void;
    dispose(): void;
}

let engine: ZenEngine | null = null;
let enginePromise: Promise<ZenEngine> | null = null;
let startToken = 0;

async function createZenEngine(): Promise<ZenEngine> {
    const Tone = await getTone();
    const transport = Tone.getTransport();
    transport.bpm.value = BPM;

    const nodes: { dispose(): void }[] = [];
    const keep = <T extends { dispose(): void }>(node: T): T => {
        nodes.push(node);
        return node;
    };

    const verb = keep(new Tone.Reverb({ decay: 12, preDelay: 0.2, wet: 0.65 }));
    const masterFilter = keep(new Tone.Filter(3800, "lowpass"));
    const masterGain = keep(new Tone.Gain(0));
    const limiter = keep(new Tone.Limiter(-1));

    verb.connect(masterFilter);
    masterFilter.chain(masterGain, limiter);
    limiter.toDestination();

    const kalimba = keep(new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "sine" },
        envelope: { attack: 0.004, decay: 1.6, sustain: 0, release: 2.2 },
    }));
    kalimba.volume.value = -12;
    const echo = keep(new Tone.FeedbackDelay({ delayTime: "8n.", feedback: 0.32, wet: 0.3 }));
    kalimba.chain(echo, verb);

    const bells = keep(new Tone.PolySynth(Tone.FMSynth, {
        harmonicity: 3.01,
        modulationIndex: 7,
        oscillator: { type: "sine" },
        envelope: { attack: 0.002, decay: 3, sustain: 0, release: 3 },
        modulationEnvelope: { attack: 0.001, decay: 0.2, sustain: 0, release: 0.1 },
    }));
    bells.volume.value = -22;
    bells.connect(verb);

    const gong = keep(new Tone.FMSynth({
        harmonicity: 1.41,
        modulationIndex: 12,
        oscillator: { type: "sine" },
        envelope: { attack: 0.02, decay: 10, sustain: 0, release: 6 },
        modulationEnvelope: { attack: 0.01, decay: 3, sustain: 0, release: 3 },
    }));
    gong.volume.value = -26;
    gong.connect(verb);

    const bowl = keep(new Tone.PolySynth(Tone.FMSynth, {
        harmonicity: 1.5,
        modulationIndex: 5,
        oscillator: { type: "sine" },
        envelope: { attack: 0.06, decay: 12, sustain: 0, release: 8 },
        modulationEnvelope: { attack: 0.03, decay: 6, sustain: 0, release: 4 },
    }));
    bowl.volume.value = -19;
    bowl.connect(verb);

    const pad = keep(new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "triangle" },
        envelope: { attack: 5, decay: 2, sustain: 0.6, release: 10 },
    }));
    pad.volume.value = -26;
    const padFilter = keep(new Tone.Filter(0, "lowpass"));
    const padLfo = keep(new Tone.LFO({ frequency: 0.03, min: 450, max: 1100 }));
    const padBreath = keep(new Tone.LFO({ frequency: 0.04, min: 0.55, max: 1 }));
    const padGain = keep(new Tone.Gain(0));
    pad.chain(padFilter, padGain, verb);
    padLfo.connect(padFilter.frequency);
    padLfo.start();
    padBreath.connect(padGain.gain);
    padBreath.start();

    let scene: Scene = { root: pick(ROOT_POOL), scale: pick(SCALES) };
    let sceneLength = randInt(4, 7);
    let bar = 0;
    let sceneApplied = false;
    let melodyRest = false;
    let density = rand(0.16, 0.28);
    let walk = 0;
    let kalimbaNotes: number[] = [];
    let bellNotes: number[] = [];

    function rebuildNotes(): void {
        kalimbaNotes = buildScaleNotes(scene.root, scene.scale, KALIMBA_RANGE);
        bellNotes = buildScaleNotes(scene.root, scene.scale, BELLS_RANGE);
        walk = reflect(walk, kalimbaNotes.length);
    }
    rebuildNotes();

    function chimeCascade(time: number): void {
        if (bellNotes.length < 2) return;
        const up = chance(0.5);
        const count = randInt(4, 8);
        let t = time + rand(0.3, 0.9);
        let idx = up ? 0 : bellNotes.length - 1;
        for (let i = 0; i < count; i++) {
            bells.triggerAttackRelease(midiToNote(bellNotes[idx]), "2n", t, rand(0.06, 0.16));
            t += rand(0.14, 0.32);
            idx += up ? 1 : -1;
            if (idx < 0 || idx >= bellNotes.length) break;
        }
    }

    function strikeBowl(time: number, velocity: number): void {
        bowl.triggerAttackRelease(midiToNote(scene.root), 10, time, velocity);
        bowl.triggerAttackRelease(midiToNote(scene.root + 12), 10, time + rand(0.04, 0.12), velocity * 0.35);
    }

    function applyScene(time: number): void {
        if (sceneApplied) {
            scene = nextScene(scene);
        }
        sceneApplied = true;
        rebuildNotes();
        density = rand(0.16, 0.28);

        strikeBowl(time + 0.2, rand(0.5, 0.7));

        const chord = [
            scene.root,
            scene.root + scene.scale[3],
            scene.root + 12,
            scene.root + 12 + scene.scale[2],
        ].map(midiToNote);
        pad.triggerAttackRelease(chord, `${sceneLength}m`, time + 0.1, rand(0.4, 0.6));

        if (chance(0.5)) {
            chimeCascade(time + 0.4);
        }
        if (chance(0.3)) {
            gong.triggerAttackRelease(midiToNote(scene.root - 12), "1m", time + rand(0.5, 2), rand(0.25, 0.45));
        }
    }

    const barLoop = keep(new Tone.Loop((time) => {
        bar++;
        melodyRest = melodyRest ? false : chance(0.12);
        if (bar >= sceneLength) {
            bar = 0;
            sceneLength = randInt(4, 7);
            applyScene(time);
        } else if (chance(0.3)) {
            strikeBowl(time + rand(0.2, 1.2), rand(0.15, 0.3));
        }
    }, "1m"));

    const melodyLoop = keep(new Tone.Loop((time) => {
        if (melodyRest || kalimbaNotes.length === 0) return;

        walk += pick([-3, -2, -1, -1, 1, 1, 2, 3]);
        if (chance(0.1)) {
            walk += pick([-7, 7]);
        }
        walk = reflect(walk, kalimbaNotes.length);

        if (chance(density)) {
            const note = kalimbaNotes[walk];
            const notes = chance(0.18)
                ? [midiToNote(note), midiToNote(note + 12)]
                : [midiToNote(note)];
            kalimba.triggerAttackRelease(notes, "4n", time + rand(0, 0.06), rand(0.25, 0.6));
        }
    }, "8n"));

    const bellLoop = keep(new Tone.Loop((time) => {
        if (bellNotes.length === 0) return;
        if (chance(0.3)) {
            bells.triggerAttackRelease(midiToNote(pick(bellNotes)), "2n", time + rand(0, 0.3), rand(0.1, 0.25));
        }
    }, "1m"));

    barLoop.start(0);
    melodyLoop.start(0);
    bellLoop.start(0);

    const withReady = verb as unknown as { ready?: Promise<void> };
    if (withReady.ready) {
        try {
            await withReady.ready;
        } catch {
            
        }
    }

    let firstStart = true;

    return {
        fadeIn() {
            if (firstStart) {
                firstStart = false;
                applyScene(Tone.now());
            }
            if (transport.state !== "started") {
                transport.start();
            }
            masterGain.gain.rampTo(1, FADE_IN);
        },
        fadeOut() {
            masterGain.gain.rampTo(0, FADE_OUT);
            transport.pause();
        },
        dispose() {
            transport.pause();
            nodes.forEach((node) => node.dispose());
        },
    };
}

function getEngine(): Promise<ZenEngine> {
    if (!enginePromise) {
        enginePromise = createZenEngine().then((zen) => {
            engine = zen;
            return zen;
        });
        enginePromise.catch(() => {
            enginePromise = null;
        });
    }
    return enginePromise;
}

if (import.meta.hot) {
    import.meta.hot.dispose(() => {
        startToken++;
        engine?.dispose();
        engine = null;
        enginePromise = null;
        isMusicPlaying.value = false;
    });
}

export function useAudio() {
    const playMusic = async () => {
        if (isMusicPlaying.value) return;
        const token = ++startToken;
        try {
            const Tone = await getTone();
            await Tone.start();
            const zen = await getEngine();
            if (token !== startToken) return;
            zen.fadeIn();
            isMusicPlaying.value = true;
        } catch (e) {
            console.warn("Generative audio failed to start:", e);
            isMusicPlaying.value = false;
        }
    };

    const pauseMusic = () => {
        startToken++;
        engine?.fadeOut();
        isMusicPlaying.value = false;
    };

    const toggleMusic = () => {
        if (isMusicPlaying.value) {
            pauseMusic();
        } else {
            void playMusic();
        }
    };

    return {
        isMusicPlaying,
        playMusic,
        pauseMusic,
        toggleMusic,
    };
}